import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import type { PunchCardTemplate } from '@/hooks/usePunchCardTemplates';

export type PunchCardStatus = 'active' | 'used' | 'expired' | 'cancelled';

export interface PortalPunchCardUsage {
  id: string;
  clips_used: number;
  used_at: string;
  session_name: string | null;
  session_date: string | null;
  session_time: string | null;
  appointment_type_name: string | null;
}

export interface PortalPunchCardItem {
  id: string;
  clinic_id: string;
  name: string;
  total_clips: number;
  used_clips: number;
  status: PunchCardStatus;
  purchased_at: string;
  expires_at: string | null;
  punch_card_number: string | null;
  usages: PortalPunchCardUsage[];
}

export const portalPunchCardsQueryKey = (patientId: string | null) =>
  ['portal', 'punch-cards', patientId] as const;

export const portalPunchCardShopQueryKey = (clinicId: string | null) =>
  ['portal', 'punch-card-shop', clinicId] as const;

export function usePortalPunchCards() {
  const { activePatientId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: portalPunchCardsQueryKey(activePatientId),
    enabled: Boolean(activePatientId),
    queryFn: async (): Promise<PortalPunchCardItem[]> => {
      if (!activePatientId) return [];

      const { data, error } = await supabase
        .from('punch_cards')
        .select('id, clinic_id, name, total_clips, used_clips, status, purchased_at, expires_at, punch_card_number')
        .eq('patient_id', activePatientId)
        .order('purchased_at', { ascending: false });

      if (error) throw error;

      const cards = (data ?? []) as Omit<PortalPunchCardItem, 'usages'>[];
      if (cards.length === 0) return [];

      const { data: usageRows, error: usageError } = await supabase
        .from('punch_card_usages')
        .select(
          'id, punch_card_id, clips_used, used_at, session_name, session_date, session_time, appointment_type_name',
        )
        .in(
          'punch_card_id',
          cards.map((card) => card.id),
        )
        .order('used_at', { ascending: false });

      if (usageError) throw usageError;

      const usagesByCard = new Map<string, PortalPunchCardUsage[]>();
      for (const row of usageRows ?? []) {
        const usage: PortalPunchCardUsage = {
          id: row.id,
          clips_used: row.clips_used,
          used_at: row.used_at,
          session_name: row.session_name,
          session_date: row.session_date,
          session_time: row.session_time,
          appointment_type_name: row.appointment_type_name,
        };
        const list = usagesByCard.get(row.punch_card_id) ?? [];
        list.push(usage);
        usagesByCard.set(row.punch_card_id, list);
      }

      return cards.map((card) => ({
        ...card,
        usages: (usagesByCard.get(card.id) ?? []).slice().sort((a, b) => {
          const aAt = Date.parse(a.session_date ?? a.used_at);
          const bAt = Date.parse(b.session_date ?? b.used_at);
          return bAt - aAt;
        }),
      }));
    },
    staleTime: 30_000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}

export function usePortalPunchCardShop() {
  const { activeClinicId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: portalPunchCardShopQueryKey(activeClinicId),
    enabled: Boolean(activeClinicId),
    queryFn: async (): Promise<PunchCardTemplate[]> => {
      if (!activeClinicId) return [];

      const { data, error } = await supabase
        .from('punch_card_templates')
        .select('*')
        .eq('clinic_id', activeClinicId)
        .eq('is_active', true)
        .eq('visible_in_portal', true)
        .gt('price_cents', 0)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return (data ?? []) as PunchCardTemplate[];
    },
    staleTime: 30_000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}

export interface PurchasePunchCardInput {
  templateId: string;
  patientId: string;
  returnPath: string;
}

export interface PurchasePunchCardResult {
  checkout_url: string;
  pay_token: string;
}

export function usePurchasePunchCard() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      patientId,
      returnPath,
    }: PurchasePunchCardInput): Promise<PurchasePunchCardResult> => {
      const { data: intentId, error } = await supabase.rpc(
        'create_punch_card_purchase_intent',
        {
          p_template_id: templateId,
          p_patient_id: patientId,
        },
      );
      if (error) throw error;
      if (!intentId) throw new Error('Could not create punch card purchase');

      const result = await callFunction<
        {
          punch_card_purchase_intent_id: string;
          success_url: string;
          cancel_url: string;
        },
        PurchasePunchCardResult
      >('pay-create-punch-card-checkout', {
        punch_card_purchase_intent_id: intentId,
        success_url: withPurchaseParam(returnPath, 'succeeded'),
        cancel_url: withPurchaseParam(returnPath, 'cancelled'),
      });

      return result;
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: portalPunchCardsQueryKey(vars.patientId) });
    },
  });
}

function withPurchaseParam(returnPath: string, status: 'succeeded' | 'cancelled'): string {
  const url = new URL(returnPath, window.location.origin);
  url.searchParams.set('purchase', status);
  return url.toString();
}

export async function syncPunchCardCheckout(payToken: string): Promise<void> {
  const token = payToken.trim();
  if (!token || token.length < 8 || token === '__PAY_TOKEN__') return;

  const { error } = await supabase.functions.invoke('pay-checkout-status', {
    body: { pay_token: token },
  });
  if (error) throw error;
}
